import { useEffect, useRef, useState } from "react";

const HOVER_SELECTOR = 'a, button, [role="button"], [data-cursor="hover"], input, textarea';

export default function CustomCursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [isTouch, setIsTouch] = useState(false);

    useEffect(() => {
        const touch = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
        setIsTouch(touch);
        if (touch) return;

        let mx = 0, my = 0;
        let rx = 0, ry = 0;
        let frame;

        const onMove = (e) => {
            mx = e.clientX;
            my = e.clientY;
            if (dotRef.current) {
                dotRef.current.style.transform = `translate3d(${mx - 4}px, ${my - 4}px, 0)`;
            }
        };

        const tick = () => {
            // Ring lags slightly
            rx += (mx - rx) * 0.18;
            ry += (my - ry) * 0.18;
            if (ringRef.current) {
                ringRef.current.style.transform = `translate3d(${rx - 16}px, ${ry - 16}px, 0)`;
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);

        const onEnter = () => {
            ringRef.current?.classList.add("scale-[1.6]", "border-tca-cyan/90");
            ringRef.current?.classList.remove("border-tca-cyan/40");
            dotRef.current?.classList.add("scale-[0.4]");
        };
        const onLeave = () => {
            ringRef.current?.classList.remove("scale-[1.6]", "border-tca-cyan/90");
            ringRef.current?.classList.add("border-tca-cyan/40");
            dotRef.current?.classList.remove("scale-[0.4]");
        };

        const attach = () => {
            document.querySelectorAll(HOVER_SELECTOR).forEach((el) => {
                el.addEventListener("mouseenter", onEnter);
                el.addEventListener("mouseleave", onLeave);
            });
        };

        window.addEventListener("mousemove", onMove);
        attach();
        // Re-attach when DOM mutates (route change, lazy mounts)
        const mo = new MutationObserver(attach);
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("mousemove", onMove);
            mo.disconnect();
            document.querySelectorAll(HOVER_SELECTOR).forEach((el) => {
                el.removeEventListener("mouseenter", onEnter);
                el.removeEventListener("mouseleave", onLeave);
            });
        };
    }, []);

    if (isTouch) return null;

    return (
        <>
            <div
                ref={ringRef}
                data-testid="custom-cursor-ring"
                aria-hidden="true"
                className="fixed top-0 left-0 z-[110] w-8 h-8 rounded-full border border-tca-cyan/40 pointer-events-none transition-[transform,border-color] duration-200 ease-out mix-blend-screen"
                style={{ willChange: "transform" }}
            />
            <div
                ref={dotRef}
                data-testid="custom-cursor-dot"
                aria-hidden="true"
                className="fixed top-0 left-0 z-[111] w-2 h-2 rounded-full bg-tca-cyan pointer-events-none transition-transform duration-150 ease-out"
                style={{
                    willChange: "transform",
                    boxShadow: "0 0 12px rgba(0,224,232,0.85)",
                }}
            />
        </>
    );
}
